import React from 'react';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import NewsletterSignUp from '../../common/Newsletter-sign';
import heroImage from '../../../assets/christmas25/grotto.webp';
import festiveImage from '../../../assets/christmas25/festive.webp';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const CASTLE_BOOKING   = '/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761';
const VILLAGE_BOOKING  = '/booking-journey?locationId=978&pageId=d4314a7f-aa55-4e17-994e-f58655da7072';

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

/* ── PAGE ── */
const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

/* ── HERO ── */
const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  height: clamp(480px, 72vh, 780px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) { height: clamp(520px, 82svh, 700px); }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center top;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: linear-gradient(to top,
    rgba(16,29,64,0.94) 0%,
    rgba(16,29,64,0.62) 32%,
    rgba(16,29,64,0.18) 60%,
    transparent 100%
  );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 42rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

/* ── SECTIONS ── */
const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  max-width: 1280px;
  margin: 0 auto;
`;

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
  text-align: ${p => p.$center ? 'center' : 'left'};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
  margin-left: ${p => p.$center ? 'auto' : '0'};
  margin-right: ${p => p.$center ? 'auto' : '0'};
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  text-align: ${p => p.$center ? 'center' : 'left'};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2rem);
  max-width: ${p => p.$center ? '52rem' : '640px'};
  text-align: ${p => p.$center ? 'center' : 'left'};
  margin-left: ${p => p.$center ? 'auto' : '0'};
  margin-right: ${p => p.$center ? 'auto' : '0'};
`;

/* ── GROTTO CARDS ── */
const GrottoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: clamp(1.5rem, 3vw, 2.5rem);
  margin-top: clamp(2rem, 4vw, 3rem);

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const GrottoCard = styled.div`
  border-radius: 20px;
  overflow: hidden;
  border: 1px solid ${C.border};
  background: #fff;
  box-shadow: 0 8px 40px rgba(16,29,64,0.08);
  display: flex;
  flex-direction: column;
`;

const GrottoCardImg = styled.div`
  position: relative;
  height: clamp(200px, 28vw, 300px);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center 20%;
    display: block;
  }
`;

const GrottoCardBadge = styled.div`
  position: absolute;
  top: 1rem;
  left: 1rem;
  padding: 0.3rem 0.8rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  background: ${C.gold};
  color: #fff;
`;

const GrottoCardBody = styled.div`
  padding: clamp(1.75rem, 3vw, 2.25rem);
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const GrottoCardKicker = styled.p`
  margin: 0 0 0.35rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.65rem;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const GrottoCardTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.5rem, 2.8vw, 2rem);
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.15;
  em { font-style: italic; color: ${C.gold}; }
`;

const GrottoCardAccent = styled.div`
  width: 40px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: 1.25rem;
  border-radius: 2px;
`;

const GrottoCardDesc = styled.p`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.9rem, 1.6vw, 0.975rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 1.5rem;
  flex: 1;
`;

const GrottoCardIncludes = styled.ul`
  list-style: none;
  margin: 0 0 1.75rem;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const GrottoCardIncludesItem = styled.li`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.875rem;
  color: ${C.navy};
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;

  &::before {
    content: '—';
    color: ${C.gold};
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 0.05rem;
  }
`;

const GrottoCardBtnRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  margin-top: auto;
`;

const BookBtn = styled.a`
  display: block;
  text-align: center;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  &:hover { background: ${C.goldDk}; }
`;

const LearnMoreBtn = styled(Link)`
  display: block;
  text-align: center;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding: 0.9rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(16,29,64,0.18);
  background: transparent;
  color: ${C.navy};
  text-decoration: none;
  &:hover { border-color: ${C.gold}; color: ${C.gold}; }
`;

/* ── COMPARISON ── */
const CompareGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1px;
  background: ${C.border};
  border-radius: 14px;
  overflow: hidden;
  margin-top: clamp(2rem, 4vw, 3rem);

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const CompareCol = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  padding: clamp(1.5rem, 3vw, 2rem);
`;

const CompareColTitle = styled.h3`
  margin: 0 0 1.25rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
  padding-bottom: 0.85rem;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(255,255,255,0.12)' : C.border};
`;

const CompareRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  padding: 0.65rem 0;
  border-bottom: 1px solid ${p => p.$light ? 'rgba(255,255,255,0.07)' : C.border};

  &:last-child { border-bottom: none; }

  &::before {
    content: '—';
    color: ${p => p.$light ? C.gold : C.gold};
    font-weight: 700;
    flex-shrink: 0;
    margin-top: 0.05rem;
  }
`;

const CompareText = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.55;
  color: ${p => p.$light ? 'rgba(255,255,255,0.75)' : C.muted};

  strong {
    color: ${p => p.$light ? '#fff' : C.navy};
    font-weight: 600;
  }
`;

/* ── CTA SECTION ── */
const CtaSection = styled.section`
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
  &:hover { background: ${C.goldDk}; }
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

/* ── COMPONENT ── */
const SantasGrottoOptions = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';

  return (
    <Page className={pageClassName}>
      <Helmet>
        <title>Santa's Grotto at Belvoir Castle 2026 — Choose Your Experience</title>
        <meta name="description" content="Two magical ways to meet Father Christmas at Belvoir this Christmas — Santa's Grotto inside the Castle as part of Deck the Halls, or at Belvoir Retail Village. Find the right experience for your family." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/santas-grotto-options" />
        <meta property="og:title" content="Santa's Grotto at Belvoir Castle 2026 — Choose Your Experience" />
        <meta property="og:description" content="Two magical ways to meet Father Christmas at Belvoir this Christmas — inside the Castle or at the Retail Village." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/santas-grotto-options" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Santa's Grotto at Belvoir Castle 2026" />
        <meta name="twitter:description" content="Two magical ways to meet Father Christmas at Belvoir — inside the Castle or at the Retail Village." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      {/* ── HERO ── */}
      <HeroWrap>
        <HeroImg src={heroImage} alt="Santa's Grotto at Belvoir Castle" />
        <HeroOverlay />
        <HeroContent>
          <HeroKicker>Belvoir Castle · Christmas 2026</HeroKicker>
          <HeroTitle>
            Santa's <em>Grotto</em>
          </HeroTitle>
          <HeroStrap>
            Two magical ways to meet Father Christmas at Belvoir this Christmas — choose the experience that's right for your family
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Present for Every Child</Pill>
            <Pill>Inside the Castle</Pill>
            <Pill>Retail Village</Pill>
            <Pill>Pre-Booking Essential</Pill>
          </BadgeRow>
        </HeroContent>
      </HeroWrap>

      {/* ── TWO GROTTO OPTIONS ── */}
      <Section $bg={C.ivory} $border>
        <Inner>
          <Kicker $center>Choose Your Experience</Kicker>
          <Divider $center />
          <SectionTitle $center>
            Two Grottos.<br /><em>One Magical Christmas.</em>
          </SectionTitle>
          <SectionLead $center>
            Whether you're visiting the Castle for a full Deck the Halls day or looking for a standalone Santa visit at the Retail Village, Father Christmas is waiting to welcome your family.
          </SectionLead>

          <GrottoGrid>

            {/* ── CASTLE GROTTO ── */}
            <GrottoCard>
              <GrottoCardImg>
                <img src={heroImage} alt="Santa's Grotto inside Belvoir Castle" />
                <GrottoCardBadge>Inside the Castle</GrottoCardBadge>
              </GrottoCardImg>
              <GrottoCardBody>
                <GrottoCardKicker>Belvoir Castle · Deck the Halls</GrottoCardKicker>
                <GrottoCardTitle>
                  Santa's Grotto<br /><em>at the Castle</em>
                </GrottoCardTitle>
                <GrottoCardAccent />
                <GrottoCardDesc>
                  The most magical of settings — a private family visit with Father Christmas nestled inside the magnificent rooms of Belvoir Castle, beautifully dressed for the festive season. An intimate, unhurried moment your family will talk about for years.
                </GrottoCardDesc>
                <GrottoCardIncludes>
                  <GrottoCardIncludesItem>Private family visit with Father Christmas</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>A special present for every child</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>Children's tickets only required</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>Full Deck the Halls Castle access included</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>The Mice Trail — included with entry</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>Add-on to Deck the Halls entry ticket</GrottoCardIncludesItem>
                </GrottoCardIncludes>
                <GrottoCardBtnRow>
                  <BookBtn href={CASTLE_BOOKING} target="_blank" rel="noopener noreferrer">
                    Book Deck the Halls + Grotto
                  </BookBtn>
                  <LearnMoreBtn to="/christmas/santas-grotto-castle">
                    Full Details
                  </LearnMoreBtn>
                </GrottoCardBtnRow>
              </GrottoCardBody>
            </GrottoCard>

            {/* ── RETAIL VILLAGE GROTTO ── */}
            <GrottoCard>
              <GrottoCardImg>
                <img src={festiveImage} alt="Santa's Grotto at Belvoir Retail Village" />
                <GrottoCardBadge>Retail Village</GrottoCardBadge>
              </GrottoCardImg>
              <GrottoCardBody>
                <GrottoCardKicker>Belvoir Retail Village · Standalone Visit</GrottoCardKicker>
                <GrottoCardTitle>
                  Santa's Grotto<br /><em>at the Village</em>
                </GrottoCardTitle>
                <GrottoCardAccent />
                <GrottoCardDesc>
                  A warm, magical grotto experience at Belvoir Retail Village — perfect on its own or as part of a full day at Belvoir. No Castle entry required. Step in, meet Father Christmas, and let the festive magic begin.
                </GrottoCardDesc>
                <GrottoCardIncludes>
                  <GrottoCardIncludesItem>Private family visit with Father Christmas</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>A special present for every child</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>No Castle entry ticket required</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>Explore shops and dining at the Retail Village</GrottoCardIncludesItem>
                  <GrottoCardIncludesItem>5-minute walk from Belvoir Castle</GrottoCardIncludesItem>
                </GrottoCardIncludes>
                <GrottoCardBtnRow>
                  <BookBtn href={VILLAGE_BOOKING} target="_blank" rel="noopener noreferrer">
                    Book Santa's Grotto
                  </BookBtn>
                  <LearnMoreBtn to="/christmas/santas-grotto-retail-village-options">
                    Full Details
                  </LearnMoreBtn>
                </GrottoCardBtnRow>
              </GrottoCardBody>
            </GrottoCard>

          </GrottoGrid>
        </Inner>
      </Section>

      {/* ── SIDE BY SIDE COMPARISON ── */}
      <Section $bg="#fff" $border>
        <Inner>
          <Kicker $center>Not Sure Which to Choose?</Kicker>
          <Divider $center />
          <SectionTitle $center>
            At a <em>Glance</em>
          </SectionTitle>
          <SectionLead $center>
            Both grottos offer a truly magical visit with Father Christmas and a present for every child. Here's how they differ.
          </SectionLead>

          <CompareGrid>
            <CompareCol>
              <CompareColTitle>Castle Grotto</CompareColTitle>
              <CompareRow>
                <CompareText><strong>Location:</strong> Inside Belvoir Castle during Deck the Halls</CompareText>
              </CompareRow>
              <CompareRow>
                <CompareText><strong>Entry:</strong> Deck the Halls ticket required — Grotto added at checkout</CompareText>
              </CompareRow>
              <CompareRow>
                <CompareText><strong>Also includes:</strong> 15 carol-themed rooms, the Mice Trail, full Castle access</CompareText>
              </CompareRow>
              <CompareRow>
                <CompareText><strong>Best for:</strong> Families wanting the full Belvoir Christmas day out</CompareText>
              </CompareRow>
              <CompareRow>
                <CompareText><strong>Present for every child</strong> included</CompareText>
              </CompareRow>
            </CompareCol>

            <CompareCol $dark>
              <CompareColTitle $light>Retail Village Grotto</CompareColTitle>
              <CompareRow $light>
                <CompareText $light><strong>Location:</strong> Belvoir Retail Village — 5 min walk from the Castle</CompareText>
              </CompareRow>
              <CompareRow $light>
                <CompareText $light><strong>Entry:</strong> No Castle ticket needed — book the Grotto on its own</CompareText>
              </CompareRow>
              <CompareRow $light>
                <CompareText $light><strong>Also includes:</strong> Access to Retail Village shops, dining and festive atmosphere</CompareText>
              </CompareRow>
              <CompareRow $light>
                <CompareText $light><strong>Best for:</strong> Families wanting a standalone Santa visit or a shorter day out</CompareText>
              </CompareRow>
              <CompareRow $light>
                <CompareText $light><strong>Present for every child</strong> included</CompareText>
              </CompareRow>
            </CompareCol>
          </CompareGrid>
        </Inner>
      </Section>

      {/* ── NEWSLETTER ── */}
      <div style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" />
      </div>

      {/* ── CTA ── */}
      <CtaSection>
        <CtaInner>
          <CtaTitle>
            Father Christmas <em>is Waiting</em>
          </CtaTitle>
          <CtaBody>
            Spaces are limited at both grottos — pre-booking is essential to avoid disappointment. Choose your experience and book now to secure your family's magical moment this Christmas.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href={CASTLE_BOOKING} target="_blank" rel="noopener noreferrer">
              Castle Grotto
            </CtaBtn>
            <CtaBtn href={VILLAGE_BOOKING} target="_blank" rel="noopener noreferrer">
              Village Grotto
            </CtaBtn>
          </CtaBtnRow>
          <CtaBtnRow style={{ marginTop: '0.75rem' }}>
            <CtaSecondary to="/christmas/your-festive-visit">
              Plan Your Visit
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Belvoir Castle · Pre-booking essential · Present for every child
          </CtaMeta>
        </CtaInner>
      </CtaSection>
    </Page>
  );
};

export default SantasGrottoOptions;
